import type { Alpha3Code } from 'i18n-iso-countries'
import type { EventHandler, KeyboardEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import { useGrantsUtils } from '../../../utils/utils'

import { useQueryList } from '@percent/utility'
import {
  Badge,
  Button,
  Copy,
  FlagAvatar,
  Flex,
  Icon,
  Menu,
  SearchInput,
  SectionProps,
  SidePanel,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
  Tooltip
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useGrantsPermissions } from '@percent/admin-dashboard/app/grants/hooks/useGrantsPermissions'
import { isValidAlpha3CountryCode } from '@percent/admin-dashboard/common/utility/getCountryFromCountryCode'
import type { ProgramCandidate } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { RecalculateCandidateModal } from './recalculateCandidateModal/RecalculateCandidateModal'
import { UpdateCandidateModal } from './updateCandidateModal/UpdateCandidateModal'
import { RejectCandidateModal } from './rejectCandidateModal/RejectCandidateModal'
import { DeleteCandidateModal } from './deleteCandidateModal/DeleteCandidateModal'
import { CandidateTasks } from './candidateTasks/CandidateTasks'
import styles from './ProgramCandidates.module.scss'

type TableSort = 'asc' | 'desc'

export const ID_TAIL_LENGTH = 7

enum CandidateAction {
  EDIT = 'edit',
  RECALCULATE = 'recalculate',
  REJECT = 'reject',
  DELETE = 'delete',
  VIEW_TASKS = 'view-tasks'
}

export function ProgramCandidates() {
  const { t } = useTranslation()
  const { id: programId } = useParams<{ id: string }>()
  const { grantProgramService } = useServices()
  const { formatDate, getStateBadge } = useGrantsUtils()

  const [canUpdateCandidate, canRejectCandidate, canDeleteCandidate] = useGrantsPermissions([
    'can_update_candidate',
    'can_reject_candidate',
    'can_delete_candidate'
  ])
  const [order] = useState<TableSort>('desc')
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch] = useDebounceValue(search, 500)
  const [activeCandidate, setActiveCandidate] = useState<{
    candidate: ProgramCandidate
    action: CandidateAction
  } | null>(null)

  const [{ data, isLoading, totalResults }, { nextPage, previousPage, refresh }] = useQueryList(
    ['/v1/admin/grants/programs', programId, 'candidates', debouncedSearch, order],
    grantProgramService.getProgramCandidates,
    {
      programId: programId ?? '',
      pageSize: 100,
      direction: order.toUpperCase() as TableColumnSortDirection,
      sortBy: 'submittedAt',
      search: debouncedSearch || undefined
    }
  )

  const rows = data as ProgramCandidate[] | null

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(event => {
    if (event.key === 'Enter') {
      const { value } = event.target as HTMLInputElement
      setSearch(value)
    }
  }, [])

  const handleClearValue = useCallback(() => {
    setSearch('')
  }, [])

  const columns: TableColumn[] = useMemo(
    () => [
      {
        id: 'country',
        content: t('table.country'),
        isSortable: false,
        props: { width: '10%' }
      },
      {
        id: 'organization',
        content: t('table.organization'),
        isSortable: false,
        props: { width: '30%' }
      },
      {
        id: 'candidateId',
        content: t('table.candidateId'),
        isSortable: false,
        props: { width: '30%' }
      },
      {
        id: 'status',
        content: t('table.status'),
        isSortable: false,
        props: { width: '15%' }
      },
      {
        id: 'submittedAt',
        content: t('table.submittedAt'),
        isSortable: false,
        props: { width: '14%' }
      },
      {
        id: 'actions',
        content: ' ',
        isSortable: false,
        props: { width: '1%', style: { textAlign: 'right' } }
      }
    ],
    [t]
  )

  const filters = useMemo(
    () => (
      <Flex w="100%" maw="480" mr={8}>
        <SearchInput
          value={search}
          handleClearValue={handleClearValue}
          onKeyDown={handleKeyPress}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('typography.search')}
          inputSize="small"
        />
      </Flex>
    ),
    [handleClearValue, handleKeyPress, search, t]
  )

  const getMenuSections = useCallback(
    (candidate: ProgramCandidate): SectionProps[] => [
      {
        items: [
          {
            key: 'view-details',
            label: t('grants.programDetails.candidates.actions.viewDetails'),
            icon: 'bank',
            dataTestId: `view-details-${candidate.id}`,
            onClick: () => window.open(`/organizations/${candidate.organisation?.id}`, '_blank')
          },
          {
            key: 'view-tasks',
            label: t('grants.programDetails.candidates.actions.viewTasks'),
            icon: 'check-badge',
            dataTestId: `view-tasks-${candidate.id}`,
            onClick: () => setActiveCandidate({ candidate, action: CandidateAction.VIEW_TASKS })
          }
        ]
      },
      {
        items: [
          ...(canUpdateCandidate
            ? ([
                {
                  key: 'edit',
                  label: t('button.edit'),
                  icon: 'edit',
                  dataTestId: `edit-${candidate?.id}`,
                  onClick: () => setActiveCandidate({ candidate, action: CandidateAction.EDIT })
                },
                {
                  key: 'recalculate',
                  label: t('grants.programDetails.candidates.actions.recalculate'),
                  icon: 'rotate-right',
                  dataTestId: `recalculate-${candidate.id}`,
                  onClick: () => setActiveCandidate({ candidate, action: CandidateAction.RECALCULATE })
                }
              ] as SectionProps['items'])
            : [])
        ]
      },
      {
        items: [
          ...(canRejectCandidate
            ? ([
                {
                  key: 'reject',
                  label: t('grants.programDetails.candidates.actions.reject'),
                  icon: 'close',
                  dataTestId: `reject-${candidate.id}`,
                  onClick: () => setActiveCandidate({ candidate, action: CandidateAction.REJECT })
                }
              ] as SectionProps['items'])
            : []),
          ...(canDeleteCandidate && !candidate.deletedAt
            ? ([
                {
                  key: 'delete',
                  label: t('grants.programDetails.candidates.actions.delete'),
                  icon: 'bin',
                  dataTestId: `delete-${candidate.id}`,
                  onClick: () => setActiveCandidate({ candidate, action: CandidateAction.DELETE })
                }
              ] as SectionProps['items'])
            : [])
        ]
      }
    ],
    [t]
  )

  const closeAllModals = useCallback(() => {
    setActiveCandidate(null)
  }, [])

  return (
    <Flex gap={16} flex={1} w="100%">
      <Flex flex={1} w="100%" direction="column" style={{ minWidth: 0, width: '100%' }}>
        <TableProvider
          columns={columns}
          emptyTableTitle={t('table.emptySearchResults')}
          illustration="no-results"
          isLoading={isLoading}
          totalResults={totalResults}
          className="w-full"
        >
          <TableSearchAndFilters>{filters}</TableSearchAndFilters>
          <Table>
            <TableBody>
              {rows?.map(candidate => {
                const { id, organisation, createdAt } = candidate

                const stateBadge = getStateBadge(candidate, t)
                const createdAtStr = typeof createdAt === 'string' ? createdAt : createdAt?.toISOString()
                const countryCode = organisation?.countryCode ?? undefined

                return (
                  <TableRow key={id} data-testid={id}>
                    <TableCell inlineStyle={{ width: '150px' }}>
                      {countryCode && isValidAlpha3CountryCode(countryCode) ? (
                        <FlagAvatar code3={countryCode as Alpha3Code} width="28px" />
                      ) : null}
                    </TableCell>
                    <TableCell>{organisation?.name ?? t('typography.notAvailable')}</TableCell>
                    <TableCell>
                      {candidate?.id ? (
                        <Copy text={candidate.id} message={t('typography.copied')}>
                          <Flex maw={250} miw={0} align="center" wrap="nowrap">
                            <Tooltip content={candidate.id}>
                              {candidate.id.length > ID_TAIL_LENGTH ? (
                                <span className={styles.idWrapper}>
                                  <span className={styles.idHead}>{candidate.id.slice(0, -ID_TAIL_LENGTH)}</span>
                                  <span className={styles.idTail}>{candidate.id.slice(-ID_TAIL_LENGTH)}</span>
                                </span>
                              ) : (
                                <span>{candidate.id}</span>
                              )}
                            </Tooltip>
                          </Flex>
                        </Copy>
                      ) : (
                        t('typography.notAvailable')
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={stateBadge.variant} icon={stateBadge.icon}>
                        {stateBadge.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(createdAtStr)}</TableCell>
                    <TableCell>
                      <Menu
                        sections={getMenuSections(candidate)}
                        customTriggerComponent={
                          <Button
                            aria-label={t('button.actions')}
                            variant="tertiary"
                            size="mini"
                            data-testid={`recalculate-candidate-${id}-menu-button`}
                          >
                            <Icon name="menu-horizontal" size="m" color="gray300" />
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <TablePagination
            nextPage={nextPage}
            previousPage={previousPage}
            totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          />
        </TableProvider>
      </Flex>

      <UpdateCandidateModal
        isOpen={activeCandidate?.action === CandidateAction.EDIT}
        programId={programId ?? ''}
        candidateId={activeCandidate?.candidate?.id ?? ''}
        refresh={refresh}
        onClose={closeAllModals}
      />
      <RecalculateCandidateModal
        isOpen={activeCandidate?.action === CandidateAction.RECALCULATE}
        programId={programId ?? ''}
        candidateId={activeCandidate?.candidate?.id ?? null}
        refresh={refresh}
        onClose={closeAllModals}
      />
      <RejectCandidateModal
        isOpen={activeCandidate?.action === CandidateAction.REJECT}
        programId={programId ?? ''}
        candidate={activeCandidate?.candidate ?? null}
        refresh={refresh}
        onClose={closeAllModals}
      />
      <DeleteCandidateModal
        isOpen={activeCandidate?.action === CandidateAction.DELETE}
        candidate={activeCandidate?.candidate ?? null}
        refresh={refresh}
        onClose={closeAllModals}
      />

      {activeCandidate?.action === CandidateAction.VIEW_TASKS && (
        <SidePanel
          title={t('grants.programDetails.candidates.tasks.title', {
            candidateName: activeCandidate?.candidate?.organisation?.name
          })}
          onClose={closeAllModals}
          style={{ maxWidth: '40%' }}
        >
          <CandidateTasks candidateId={activeCandidate?.candidate?.id ?? ''} />
        </SidePanel>
      )}
    </Flex>
  )
}
